/**
 * AddToProjectDrawer Component
 */

import { pushNotification } from '../../../../redux/actions/notifications';
import { pushPostAuthenticationRequestCreateProject } from '../../../../redux/actions/postAuthenticationRequests';
import { requestAddTrackToProject, requestCreateProject } from '../../../../redux/actions/projects';
import { showSignUpForm, toggleAddToProjectVisibility } from '../../../../redux/actions/ui';
import { createHref } from '../../../../utils/router';
import './styles.scss';

import { last } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect, DispatchProp } from 'react-redux';
import { VelocityTransitionGroup } from 'velocity-react';

import * as nextTick from 'next-tick';
import { QueryStringContext } from '../../../../contexts/queryString';
import { IQueryStringState } from '../../../../contexts/queryString/types';
import Button from '../../../atoms/buttons/Button';
import Badge from '../../../atoms/feedback/Badge';
import TextField from '../../../atoms/forms/TextField';
import { IconProject, IconProjectSelected } from '../../../atoms/images/Icon';
import Heading from '../../../atoms/text/Heading';
import { Span } from '../../../atoms/text/Label';
import Drawer, { DrawerProps } from '../../../molecules/modals/Drawer';

interface IOwnStateProps {
  location: IRouterLocation;
  trackId: string;
  trackVersion: string;
  viewerId: string | undefined;
  isUserLoggedIn: boolean;
}

interface IOwnDispatchProps extends Required<DispatchProp<any>> {
  close(): void;
  signUpAndCreateProject(
    projectName: string,
    trackId: string,
    trackVersion: string,
    searchState: IQueryStringState,
    location: IRouterLocation,
  ): void;
}

interface IQueryStringContextProps {
  searchState: IQueryStringState;
}

interface IOwnMergedProps {
  signUpAndCreateProject: (projectName: string) => void;
}

interface IOwnProps extends Omit<DrawerProps, 'forwardedRef'> {
  projects: GQL.IProject[];
}

export type AddToProjectDrawerProps =
  & IOwnProps
  & IOwnStateProps
  & IOwnDispatchProps
  & IOwnMergedProps
  & IQueryStringContextProps;

interface IOwnState {
  isOpen: boolean;
}

const classes = new BEM('add-to-project-drawer');

class AddToProjectDrawer extends React.Component<AddToProjectDrawerProps, IOwnState> {
  constructor(props) {
    super(props);

    this.state = {
      isOpen: true,
    };
  }

  onAddToNewProject = async (name: string) => {
    if (!this.props.isUserLoggedIn) {
      this.setState({ isOpen: false }, () => {
        this.props.signUpAndCreateProject(name);
      });
      return;
    }

    this.props.dispatch(requestCreateProject({
      input: { name },
      promise: {
        resolve: (response, errors) => {
          if (errors) {
            this.props.dispatch(pushNotification({
              autoDismiss: false,
              message: 'An error occurred while saving this track to a new playlist. Please try again.',
              state: 'error',
            }));
          } else {
            this.onAddToProject(response!.createProject!.new_project_edge!.node!.id, true);
          }
        },
        reject: () => {
          this.props.dispatch(pushNotification({
            autoDismiss: false,
            message: 'An error occurred while saving this track to a new playlist. Please try again.',
            state: 'error',
          }));
        },
      },
    }));
  }

  onAddToProject = async (id: string, isNewProject = false) => {
    const licenseParams = this.props.searchState.license_params;
    this.props.dispatch(requestAddTrackToProject({
      licenseParams: isNewProject && licenseParams ? licenseParams : undefined,
      projectId: id,
      trackId: this.props.trackId!,
      trackVersion: this.props.trackVersion,
      promise: {
        resolve: (result) => {
          if (result) {
            const trackTitle = last(result!.updateProject!.project!.tracks!.edges)!.node!.title;
            this.setState({ isOpen: false });
            this.props.dispatch(pushNotification({
              message: `Track <em>${trackTitle}</em> was saved to your playlist!`,
              state: 'success',
            }));
          } else {
            this.props.dispatch(pushNotification({
              autoDismiss: false,
              message: 'An error occurred while saving this track to your playlist. Please try again.',
              state: 'error',
            }));
          }
        },
        reject: () => {
          this.props.dispatch(pushNotification({
            autoDismiss: false,
            message: 'An error occurred while saving this track to your playlist. Please try again.',
            state: 'error',
          }));
        },
      },
    }));
  }

  renderProjectButton = (project: GQL.IProject) => {
    const hasTrack = this.projectHasTrack(project);
    const tracks = project.tracks;

    return (
      <VelocityTransitionGroup
        enter={{ animation: 'callout.flash' }}
        component={Button}
        key={project.id}
        className="drawer__button-list__button"
        icon={hasTrack ? <IconProjectSelected /> : <IconProject />}
        label={<Span text={project.name} maxTextLength={25} />}
        onClick={() => this.onAddToProject(project.id)}
      >
        {tracks && (
          <Badge
            key={`project-track-count-${tracks.edges!.length}`}
            label={`${tracks.edges!.length}`}
            colorScheme="light"
          />
        )}
      </VelocityTransitionGroup>
    );
  }

  render() {
    const { isUserLoggedIn, projects } = this.props;
    return (
      <Drawer
        closeButtonLabel="Cancel"
        onClose={() => this.props.close()}
        open={this.state.isOpen}
        headerIcon={null}
        position="right"
        floatingHeader
        {...classes() }
      >
        <div {...classes('create-project', '', 'drawer__content-group') }>
          <Heading is="h4">Save to<br/>New Playlist:</Heading>
          <TextField
            name="create-project"
            placeholder="Playlist Name"
            inlineButton={<Button label="Create" colorScheme="dark" onClick={this.onAddToNewProject} />}
            colorScheme="light"
            submitOnEnter
            withoutBorder
            {...classes('create-project__input')}
          />
        </div>
        {isUserLoggedIn && !!projects.length && (
          <div {...classes('project-list', '', 'drawer__button-list') } data-title="Save to an existing playlist:">
            {projects.map(this.renderProjectButton)}
          </div>
        )}
      </Drawer>
    );
  }

  private projectHasTrack(project: GQL.IProject) {
    return !!project.tracks && !!project.tracks.edges!.find(({ node }) => node!.id === this.props.trackId);
  }
}

// tslint:disable-next-line:max-line-length
const ConnectedAddToProjectDrawer = connect<IOwnStateProps, IOwnDispatchProps, IOwnProps & IQueryStringContextProps, AddToProjectDrawerProps, IStoreState>(
  ({ found, ui: { addToProject }, user: { isLoggedIn, viewerId } }) => ({
    isUserLoggedIn: isLoggedIn,
    location: found.match.location,
    trackId: addToProject ? addToProject.trackId! : '',
    trackVersion: addToProject ? addToProject.trackVersion! : '',
    viewerId,
  }),
  (dispatch) => ({
    dispatch,
    close: () => {
      dispatch(toggleAddToProjectVisibility(false));
    },
    signUpAndCreateProject: (projectName, trackId, trackVersion, searchState, location) => {
      dispatch(pushPostAuthenticationRequestCreateProject({
        page: createHref(location),
        projectName,
        trackId,
        trackVersion,
        searchState,
      }));
      nextTick(() => dispatch(showSignUpForm({ reason: 'project' })));
    },
  }),
  (stateProps, { signUpAndCreateProject, ...dispatchProps }, ownProps) => ({
    ...stateProps,
    ...dispatchProps,
    ...ownProps,
    signUpAndCreateProject: (projectName) =>
      signUpAndCreateProject(projectName, stateProps.trackId, stateProps.trackVersion,
        ownProps.searchState, stateProps.location),
  }),
)(AddToProjectDrawer);

const AddToProjectDrawerWithQueryStringContext: React.SFC<IOwnProps> = (props) => (
  <QueryStringContext.Consumer>
    {({ filters, license_params, query }) => (
      <ConnectedAddToProjectDrawer
        searchState={{ filters, license_params, query }}
        {...props}
      />
    )}
  </QueryStringContext.Consumer>
);

export default AddToProjectDrawerWithQueryStringContext;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/modals/AddToProjectDrawer/index.tsx